import { useState, useEffect } from 'react';
import { X, EyeOff, Check, CheckCheck, RefreshCw, Trash2 } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useLocation } from 'wouter';
import { showApiError, showApiSuccess } from '@/lib/toast-utils';

export default function NotificationsPage() {
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification, 
    hideNotification,
    refreshNotifications, 
    isLoading 
  } = useNotifications();
  const [, setLocation] = useLocation();
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    refreshNotifications();
  }, [refreshNotifications]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshNotifications();
      showApiSuccess('Notificações atualizadas');
    } catch (error) {
      showApiError('Erro ao atualizar notificações');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleNotificationClick = (notification: any) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      setLocation(notification.link);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteNotification(id);
      showApiSuccess('Notificação excluída');
    } catch (error) {
      showApiError('Erro ao excluir notificação');
    }
  };

  const handleHide = async (id: string) => {
    try {
      await hideNotification(id);
      showApiSuccess('Notificação ocultada');
    } catch (error) {
      showApiError('Erro ao ocultar notificação');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      showApiSuccess('Todas as notificações foram marcadas como lidas');
    } catch (error) {
      showApiError('Erro ao marcar notificações como lidas');
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando notificações...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Notificações</h1>
          <p className="text-muted-foreground mt-1">
            {notifications.length === 0 
              ? 'Nenhuma notificação' 
              : `${notifications.length} notificação${notifications.length !== 1 ? 'ões' : ''}${unreadCount > 0 ? ` • ${unreadCount} não lida${unreadCount !== 1 ? 's' : ''}` : ''}`
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
            className="gap-2"
          >
            <RefreshCw className={cn("h-4 w-4", (isRefreshing || isLoading) && "animate-spin")} />
            Atualizar
          </Button>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="gap-2"
            >
              <CheckCheck className="h-4 w-4" />
              Marcar todas como lidas
            </Button>
          )}
        </div>
      </div>

      {notifications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="rounded-full bg-muted p-4 mb-4">
              <RefreshCw className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Nenhuma notificação</h3>
            <p className="text-muted-foreground text-center max-w-md">
              Você não tem notificações no momento. Quando houver novas notificações, elas aparecerão aqui.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifications.map((notification) => (
            <Card
              key={notification.id}
              className={cn(
                "transition-all hover:shadow-md cursor-pointer",
                !notification.read && "border-primary/50 bg-primary/5"
              )}
              onClick={() => handleNotificationClick(notification)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className={cn(
                        "text-sm font-semibold",
                        !notification.read && "text-primary"
                      )}>
                        {notification.title}
                      </h3>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: ptBR })}
                      </span>
                      {!notification.read && (
                        <Badge variant="default" className="h-4 px-1.5 text-[9px]">
                          Nova
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {notification.message}
                    </p>
                    {notification.link && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 mt-1 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLocation(notification.link!);
                        }}
                      >
                        Ver detalhes →
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!notification.read && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification.id);
                        }}
                        title="Marcar como lida"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(notification.id);
                      }}
                      title="Excluir"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHide(notification.id);
                      }}
                      title="Ocultar"
                    >
                      <EyeOff className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

